import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { MDBDataTableV5 } from 'mdbreact';
import { CircularProgress, Checkbox, TextField, Autocomplete, Drawer, Pagination, Box, Typography, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import axios from 'axios';
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content';
import DatePicker from "react-datepicker";
import EditIcon from '@mui/icons-material/Edit';
import { Modal } from 'react-bootstrap';
import { styled } from '@mui/material/styles';
import { medicationSkuList } from "../dynamicController";

// components
import Sidebar from "./components/sidebar";
import Header from "./components/header";

const mySwal = withReactContent(Swal);

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-20%`,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
      maxWidth: "80%"
    }),
  }),
);

const PendingRedemptionReport = ({ role, token }) => {

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [productList, setProductList] = useState([]);
  const [editModal, setEditModal] = useState(false);
  const [redemptionId, setRedemptionId] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountCode, setAccountCode] = useState("");
  const [patientId, setPatientId] = useState("");
  const [sku, setSku] = useState("");
  const [status, setStatus] = useState("");
  const [remarks, setRemarks] = useState("");
  const [reassignment, setReassignment] = useState("");
  const [reassignmentList, setReassignmentList] = useState([]);
  const [validationStatus, setValidationStatus] = useState("");
  const [oriStatus, setOriStatus] = useState("");
  const [openSidebar, setOpenSidebar] = useState(true);
  const [reassignmentHistory, setReassignmentHistory] = useState("");
  const [showIcMrn, setShowIcMrn] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50, total: 0, totalPages: 0, hasMore: false });
  const [search, setSearch] = useState("");
  const handleSearch = (e) => {
    console.log("e.target.value", e.target.value)
    setSearch(e.target.value)
  };

  const fetchData = useCallback((page = 1, customPageSize = 50, searchTerm = "") => {
    setLoading(true);
    const pageSize = customPageSize;

    let sendThis = {
      role,
      token,
      productList,
      page,
      pageSize,
      search: searchTerm
    }

    if (startDate && endDate) {
      sendThis.startDate = new Date(startDate).getFullYear() + "-" + (new Date(startDate).getMonth() + 1) + "-" + new Date(startDate).getDate()
      sendThis.endDate = new Date(endDate).getFullYear() + "-" + (new Date(endDate).getMonth() + 1) + "-" + new Date(endDate).getDate()
    }

    return axios.post('/api/admin/pendingRedemption', sendThis)
      .then(res => {
        if (res.data.showIcMrn) {
          setShowIcMrn(res.data.showIcMrn)
        }
        setData(res.data.data);
        setPagination(res.data.pagination);
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        if (err.response && err.response.data.error === "Token Expired") {
          mySwal.fire({ title: "Error", text: err.response.data.error, icon: "error", allowOutsideClick: false })
            .then(click => {
              if (click.isConfirmed) {
                navigate('/admin/login');
              }
            })
        } else {
          mySwal.fire("Error", err.response.data.error, "error")
        }
      });
  }, [role, token, productList, startDate, endDate, navigate]);

  useEffect(() => {
    if (role && token) {
      fetchData(1, 50);
    } else {
      navigate('/admin/login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDate = dates => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  }

  const handleFilter = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchData(1, pagination.pageSize);
  }

  const handleReset = () => {
    setSearch("");
    setStartDate("");
    setEndDate("");
    setProductList([]);
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchData(1, pagination.pageSize);
  }

  const handlePageChange = (event, newPage) => {
    fetchData(newPage, pagination.pageSize, search);
  }

  const handlePageSizeChange = (event) => {
    const newPageSize = parseInt(event.target.value);
    setPagination(prev => ({ ...prev, pageSize: newPageSize, page: 1 }));
    fetchData(1, newPageSize, search);
  }

  const handleExport = () => {
    setLoading(true);

    let sendThis = {
      role,
      token,
      productList,
      exportAll: true,
      search
    }

    if (startDate && endDate) {
      sendThis.startDate = new Date(startDate).getFullYear() + "-" + (new Date(startDate).getMonth() + 1) + "-" + new Date(startDate).getDate()
      sendThis.endDate = new Date(endDate).getFullYear() + "-" + (new Date(endDate).getMonth() + 1) + "-" + new Date(endDate).getDate()
    }

    axios.post('/api/admin/pendingRedemption', sendThis)
      .then(res => {
        setLoading(false);

        const allData = res.data.data;
        const csvRows = [];

        for (let i = 0; i < allData.length; i++) {
          const csvObject = {
            hcpType: allData[i].hcpType,
            accountName: allData[i].accountName,
            accountCode: allData[i].accountCode,
            patientId: allData[i].patientId,
            sku: allData[i].sku,
            releaseDate: allData[i].releaseDate,
            dueForCollection: allData[i].dueForCollection,
            quantityToRedeem: allData[i].quantityToRedeem,
            redemptionStatus: allData[i].redemptionStatus,
            se: allData[i].se,
            remarks: allData[i].remarks,
            patientReassign: allData[i].patientReassign,
            patientMobile: allData[i].patientMobile,
            patientIc: allData[i].patientIc,
            patientMrn: allData[i].patientMrn
          }
          if (role === "dev" || role === "hcp") {
            csvObject.patientMobile = `=""${allData[i].patientMobile}""`;
          }
          csvRows.push(csvObject);
        }

        let headers;
        if (role === "hcp" && showIcMrn) {
          headers = [
            { label: "HCP Type", key: "hcpType" },
            { label: "Account Name", key: "accountName" },
            { label: "Account Code", key: "accountCode" },
            { label: "Patient ID", key: "patientId" },
            { label: "Redeemed SKU", key: "sku" },
            { label: "Redemption Code Release Date", key: "releaseDate" },
            { label: "Due for Collection", key: "dueForCollection" },
            { label: "Quantity To Redeem", key: "quantityToRedeem" },
            { label: "Redemption Status", key: "redemptionStatus" },
            { label: "Patient Mobile", key: "patientMobile" },
            { label: "Patient IC", key: "patientIc" },
            { label: "Patient MRN", key: "patientMrn" },
            { label: "SE Name", key: "se" },
            { label: "Remarks", key: "remarks" },
            { label: "Patient Re-assign", key: "patientReassign" }
          ];
        } else if (role === "dev" || role === "hcp") {
          headers = [
            { label: "HCP Type", key: "hcpType" },
            { label: "Account Name", key: "accountName" },
            { label: "Account Code", key: "accountCode" },
            { label: "Patient ID", key: "patientId" },
            { label: "Redeemed SKU", key: "sku" },
            { label: "Redemption Code Release Date", key: "releaseDate" },
            { label: "Due for Collection", key: "dueForCollection" },
            { label: "Quantity To Redeem", key: "quantityToRedeem" },
            { label: "Redemption Status", key: "redemptionStatus" },
            { label: "Patient Mobile", key: "patientMobile" },
            { label: "Patient MRN", key: "patientMrn" },
            { label: "SE Name", key: "se" },
            { label: "Remarks", key: "remarks" },
            { label: "Patient Re-assign", key: "patientReassign" }
          ];
        } else {
          headers = [
            { label: "HCP Type", key: "hcpType" },
            { label: "Account Name", key: "accountName" },
            { label: "Account Code", key: "accountCode" },
            { label: "Patient ID", key: "patientId" },
            { label: "Patient MRN/IC", key: "patientMrn" },
            { label: "Redeemed SKU", key: "sku" },
            { label: "Redemption Code Release Date", key: "releaseDate" },
            { label: "Due for Collection", key: "dueForCollection" },
            { label: "Quantity To Redeem", key: "quantityToRedeem" },
            { label: "Redemption Status", key: "redemptionStatus" },
            { label: "SE Name", key: "se" },
            { label: "Remarks", key: "remarks" },
            { label: "Patient Re-assign", key: "patientReassign" }
          ];
        }

        const headerRow = headers.map(h => `"${h.label}"`).join(",");
        const dataRows = csvRows.map(row =>
          headers.map(h => {
            const val = row[h.key];
            return val !== undefined && val !== null ? `"${String(val).replace(/"/g, '""')}"` : '""';
          }).join(",")
        );
        const csvContent = [headerRow, ...dataRows].join("\n");

        const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8; " });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `pending_redemption_report_${new Date().getFullYear()}_${new Date().getMonth() + 1}_${new Date().getDate()}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      })
      .catch(err => {
        setLoading(false);
        if (err.response && err.response.data.error === "Token Expired") {
          mySwal.fire({ title: "Error", text: err.response.data.error, icon: "error", allowOutsideClick: false })
            .then(click => {
              if (click.isConfirmed) {
                navigate('/admin/login');
              }
            })
        } else {
          mySwal.fire("Error", "Export failed", "error")
        }
      });
  }

  const showEditModal = (redemptionId, accountName, accountCode, patientId, sku, status, remarks, reassignment, reassignmentList, validationStatus) => {
    setRedemptionId(redemptionId);
    setAccountName(accountName);
    setAccountCode(accountCode);
    setPatientId(patientId);
    setSku(sku);
    setStatus(status);
    setOriStatus(status);
    if (remarks !== "-") {
      setRemarks(remarks);
    } else {
      setRemarks("Collection Overdue");
    }
    if (reassignment !== "-") {
      setReassignmentHistory(reassignment);
    }
    setReassignmentList(reassignmentList);
    setValidationStatus(validationStatus);
    setEditModal(true);
  }

  const hideEditModal = () => {
    setRedemptionId("");
    setAccountName("");
    setAccountCode("");
    setPatientId("");
    setSku("");
    setStatus("");
    setOriStatus("");
    setRemarks("");
    setReassignment("");
    setReassignmentHistory("");
    setReassignmentList([]);
    setValidationStatus("");
    setEditModal(false);
  }

  const reassignRedemption = () => {
    if (reassignmentHistory) {
      mySwal.fire("Error", "This redemption have reassigned", "error")
    } else {
      if (validationStatus === "Approve" || validationStatus === "Reassign") {
        if (oriStatus === "Expired") {
          if (status === "Re-Allocate") {
            if (remarks) {
              if (reassignment) {
                setLoading(true);
                axios.post("/api/admin/reassignRedemption", { role, token, redemptionId, remarks, reassignment })
                  .then(res => {
                    setRedemptionId("");
                    setAccountName("");
                    setAccountCode("");
                    setPatientId("");
                    setSku("");
                    setStatus("");
                    setOriStatus("");
                    setRemarks("");
                    setReassignment("");
                    setReassignmentHistory("");
                    setReassignmentList([]);
                    setValidationStatus("");
                    setEditModal(false);
                    fetchData(pagination.page, pagination.pageSize, search);
                  })
                  .catch(err => {
                    setLoading(false);
                    if (err.response && err.response.data.error === "Token Expired") {
                      mySwal.fire({ title: "Error", text: err.response.data.error, icon: "error", allowOutsideClick: false })
                        .then(click => {
                          if (click.isConfirmed) {
                            navigate('/admin/login');
                          }
                        })
                    } else {
                      mySwal.fire("Error", err.response && err.response.data ? err.response.data.error : "Request failed", "error")
                    }
                  });
              } else {
                mySwal.fire("Error", "Please choose the reassignment", "error")
              }
            } else {
              mySwal.fire("Error", "Please choose the remarks", "error")
            }
          } else {
            mySwal.fire("Error", "Please choose the status to Re-Allocate", "error")
          }
        } else {
          mySwal.fire("Error", "Only expired redemption can re-allocate", "error")
        }
      } else {
        mySwal.fire("Error", "Only redemption validation status is approve can reassignment", "error")
      }
    }
  }

  let tableData = [];

  if (data) {
    for (let i = 0; i < data.length; i++) {
      const dataObject = {
        hcpType: data[i].hcpType,
        accountName: data[i].accountName,
        accountCode: data[i].accountCode,
        patientId: data[i].patientId,
        sku: data[i].sku,
        releaseDate: data[i].releaseDate,
        dueForCollection: data[i].dueForCollection,
        quantityToRedeem: data[i].quantityToRedeem,
        redemptionStatus: data[i].redemptionStatus,
        se: data[i].se,
        remarks: data[i].remarks,
        patientReassign: data[i].patientReassign,
        patientMobile: data[i].patientMobile,
        patientIc: data[i].patientIc,
        patientMrn: data[i].patientMrn,
        action: (
          <div className="text-center" onClick={() => showEditModal(data[i].redemptionId, data[i].accountName, data[i].accountCode, data[i].patientId, data[i].sku, data[i].redemptionStatus, data[i].remarks, data[i].patientReassign, data[i].reassignmentList, data[i].validationStatus)}>
            <EditIcon />
          </div>
        )
      }
      tableData.push(dataObject);
    }
  }

  let devTableData = {
    columns: [
      { field: "hcpType", label: "HCP Type", sort: "asc" },
      { field: "accountName", label: "Account Name", sort: "asc" },
      { field: "accountCode", label: "Account Code", sort: "asc" },
      { field: "patientId", label: "Patient ID", sort: "asc" },
      { field: "sku", label: "Redeemed SKU", sort: "asc" },
      { field: "releaseDate", label: "Redemption Code Release Date", sort: "asc" },
      { field: "dueForCollection", label: "Due for Collection", sort: "asc" },
      { field: "quantityToRedeem", label: "Quantity To Redeem", sort: "asc" },
      { field: "redemptionStatus", label: "Redemption Status", sort: "asc" },
      { field: "patientMobile", label: "Patient Mobile", sort: "asc" },
      { field: "patientMrn", label: "Patient MRN", sort: "asc" },
      { field: "se", label: "SE Name", sort: "asc" },
      { field: "remarks", label: "Remarks", sort: "asc" },
      { field: "patientReassign", label: "Patient Re-assign", sort: "asc" },
      { field: "action", label: "Action", sort: "asc" },
    ],
    rows: tableData ? tableData : []
  };

  let hcpTableData = {
    columns: [
      { field: "hcpType", label: "HCP Type", sort: "asc" },
      { field: "accountName", label: "Account Name", sort: "asc" },
      { field: "accountCode", label: "Account Code", sort: "asc" },
      { field: "patientId", label: "Patient ID", sort: "asc" },
      { field: "sku", label: "Redeemed SKU", sort: "asc" },
      { field: "releaseDate", label: "Redemption Code Release Date", sort: "asc" },
      { field: "dueForCollection", label: "Due for Collection", sort: "asc" },
      { field: "quantityToRedeem", label: "Quantity To Redeem", sort: "asc" },
      { field: "redemptionStatus", label: "Redemption Status", sort: "asc" },
      { field: "patientMobile", label: "Patient Mobile", sort: "asc" },
      { field: "patientMrn", label: "Patient MRN", sort: "asc" },
      { field: "se", label: "SE Name", sort: "asc" },
      { field: "remarks", label: "Remarks", sort: "asc" },
      { field: "patientReassign", label: "Patient Re-assign", sort: "asc" }
    ],
    rows: tableData ? tableData : []
  };

  let hcpIcMrnTableData = {
    columns: [
      { field: "hcpType", label: "HCP Type", sort: "asc" },
      { field: "accountName", label: "Account Name", sort: "asc" },
      { field: "accountCode", label: "Account Code", sort: "asc" },
      { field: "patientId", label: "Patient ID", sort: "asc" },
      { field: "sku", label: "Redeemed SKU", sort: "asc" },
      { field: "releaseDate", label: "Redemption Code Release Date", sort: "asc" },
      { field: "dueForCollection", label: "Due for Collection", sort: "asc" },
      { field: "quantityToRedeem", label: "Quantity To Redeem", sort: "asc" },
      { field: "redemptionStatus", label: "Redemption Status", sort: "asc" },
      { field: "patientMobile", label: "Patient Mobile", sort: "asc" },
      { field: "patientIc", label: "Patient IC", sort: "asc" },
      { field: "patientMrn", label: "Patient MRN", sort: "asc" },
      { field: "se", label: "SE Name", sort: "asc" },
      { field: "remarks", label: "Remarks", sort: "asc" },
      { field: "patientReassign", label: "Patient Re-assign", sort: "asc" }
    ],
    rows: tableData ? tableData : []
  };

  let adminTableData = {
    columns: [
      { field: "hcpType", label: "HCP Type", sort: "asc" },
      { field: "accountName", label: "Account Name", sort: "asc" },
      { field: "accountCode", label: "Account Code", sort: "asc" },
      { field: "patientId", label: "Patient ID", sort: "asc" },
      { field: "sku", label: "Redeemed SKU", sort: "asc" },
      { field: "releaseDate", label: "Redemption Code Release Date", sort: "asc" },
      { field: "dueForCollection", label: "Due for Collection", sort: "asc" },
      { field: "quantityToRedeem", label: "Quantity To Redeem", sort: "asc" },
      { field: "redemptionStatus", label: "Redemption Status", sort: "asc" },
      { field: "se", label: "SE Name", sort: "asc" },
      { field: "remarks", label: "Remarks", sort: "asc" },
      { field: "patientReassign", label: "Patient Re-assign", sort: "asc" },
      { field: "action", label: "Action", sort: "asc" },
    ],
    rows: tableData ? tableData : []
  };

  let salesTableData = {
    columns: [
      { field: "hcpType", label: "HCP Type", sort: "asc" },
      { field: "accountName", label: "Account Name", sort: "asc" },
      { field: "accountCode", label: "Account Code", sort: "asc" },
      { field: "patientId", label: "Patient ID", sort: "asc" },
      { field: "sku", label: "Redeemed SKU", sort: "asc" },
      { field: "releaseDate", label: "Redemption Code Release Date", sort: "asc" },
      { field: "dueForCollection", label: "Due for Collection", sort: "asc" },
      { field: "quantityToRedeem", label: "Quantity To Redeem", sort: "asc" },
      { field: "redemptionStatus", label: "Redemption Status", sort: "asc" },
      { field: "se", label: "SE Name", sort: "asc" },
      { field: "remarks", label: "Remarks", sort: "asc" },
      { field: "patientReassign", label: "Patient Re-assign", sort: "asc" }
    ],
    rows: tableData ? tableData : []
  };

  return (
    <div className="d-flex">

      <Drawer
        sx={{
          minWidth: "20%",
          '& .MuiDrawer-paper': {
            minWidth: "20%",
            boxSizing: 'border-box',
            minHeight: "100vh",
            backgroundColor: "#FE7072",
          },
        }}
        anchor="left"
        open={openSidebar}
        variant="persistent">
        <Sidebar />
      </Drawer>

      <Main open={openSidebar}>
        <div className="mt-3 px-3">
          <Header title="PENDING REDEMPTION REPORT" openSidebar={openSidebar} setOpenSidebar={setOpenSidebar} />

          <div className="report-header mt-3 d-flex py-3 px-3 justify-content-between">
            <div className="col-4">
              <p>This report shows patient's redemptions status that is either pending to be close by healthcare providers in the system or has expired.</p>
            </div>
            <div>
              <div className="d-flex">
                <div>
                  <DatePicker placeholderText="Date" value={startDate ? `${startDate ? new Date(startDate).toLocaleDateString('en-GB') : ""} - ${endDate ? new Date(endDate).toLocaleDateString('en-GB') : ""}` : ""} onChange={handleDate} startDate={startDate} endDate={endDate} selectsRange shouldCloseOnSelect={false} className="form-control py-3" />
                </div>
                <div className="ps-2">
                  <Autocomplete
                    multiple
                    id="sku"
                    options={medicationSkuList}
                    disableCloseOnSelect
                    value={productList}
                    onChange={(e, value) => {
                      setProductList(value);
                    }}
                    renderOption={(props, medicationSkuList, { selected }) => (
                      <li {...props}>
                        <Checkbox
                          checked={selected}
                        />
                        {medicationSkuList}
                      </li>
                    )}
                    style={{ width: 250, backgroundColor: "white" }}
                    renderInput={(params) => (
                      <TextField {...params} label="SKU" />
                    )}
                  />
                </div>
                <div className="ps-2">
                  <button type="button" className="btn btn-primary shadow-none admin-button" onClick={() => handleFilter()}>Filter</button>
                </div>
                <div className="ps-2">
                  <button type="button" className="btn btn-primary shadow-none admin-button" onClick={() => handleReset()}>Reset</button>
                </div>
                <div className="ps-2">
                  <button type="button" className="btn btn-primary shadow-none admin-button" onClick={handleExport}>Export</button>
                </div>
              </div>
            </div>
          </div>
          <div className="report-header d-flex px-3 justify-content-between">
            <div>
              <p>*<span style={{ color: "green" }}>Pending</span>: Kindly contact your patients and be reminded to conduct closure in the system upon dispensing the complimentary boxes. Each redemption code only valid for 90 days.</p>
            </div>
            <div>
              <p>**<span style={{ color: "red" }}>Expired</span>: Redemptions that are failed to redeem within 90 days. Viatris will collect back the uncollected redemption stocks from hospital. Kindly contact your Viatris representative if patient had collected the stocks without completing redemption process in the system.</p>
            </div>
          </div>
          {
            loading ? (
              <div className="text-center">
                <CircularProgress />
              </div>
            ) : (
              <div>
                <MDBDataTableV5 className="pagination-table" data={role === "hcp" && showIcMrn ? hcpIcMrnTableData : role === "dev" ? devTableData : role === "admin" ? adminTableData : role === "sales" ? salesTableData : hcpTableData} hover responsive bordered paging={false} 
                  displayEntries={false}
							    entries={false}
							    entriesOptions={false}
							    info={false}
                  paginationLabel={[false, false]} />
                <div className="d-flex-col justify-content-start align-items-center gap-2 w-100">
                  <h3 className="mb-0">Search</h3>
                  <div className="d-flex justify-content-start align-items-center gap-2 w-100 gap-2">
                    <input className="w-25" type="text" value={search} onChange={handleSearch} style={{ height: "38px" }}></input>
                    <button type="button" className="btn btn-primary shadow-none admin-button" onClick={() => { setPagination(prev => ({ ...prev, page: 1 })); fetchData(1, pagination.pageSize, search); }}>Search</button>
                  </div>
                  </div>
                {pagination.total > 0 && (
                  <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, marginBottom: 4, backgroundColor: "#DEDDDD" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel>Per Page</InputLabel>
                        <Select value={pagination.pageSize} label="Per Page" onChange={handlePageSizeChange}>
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={25}>25</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                          <MenuItem value={100}>100</MenuItem>
                        </Select>
                      </FormControl>
                      <Typography variant="body2" color="textSecondary">
                        Showing {data.length} of {pagination.total} records
                      </Typography>
                    </Box>
                    <Pagination
                      count={pagination.totalPages}
                      page={pagination.page}
                      onChange={handlePageChange}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                )}
              </div>
            )
          }
        </div>
      </Main>

      <Modal show={editModal} onHide={hideEditModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Patient Re-Assignment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="container">
            <div>
              <label htmlFor="accountName">Account Name</label>
              <input
                name="accountName"
                type="text"
                value={accountName}
                className="form-control"
                readOnly
              />
            </div>
            <div className="pt-3">
              <label htmlFor="accountCode">Account Code</label>
              <input
                name="accountCode"
                type="text"
                value={accountCode}
                className="form-control"
                readOnly
              />
            </div>
            <div className="pt-3">
              <label htmlFor="patientId">Patient ID</label>
              <input
                name="patientId"
                type="text"
                value={patientId}
                className="form-control"
                readOnly
              />
            </div>
            <div className="pt-3">
              <label htmlFor="sku">SKU</label>
              <input
                name="sku"
                type="text"
                value={sku}
                className="form-control"
                readOnly
              />
            </div>
            <div className="pt-3">
              <label htmlFor="status">Status</label>
              <select className="form-select" name="status" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="Pending" disabled>Pending</option>
                <option value="Expired" disabled>Expired</option>
                <option value="Re-Allocate">Re-Allocate</option>
              </select>
            </div>
            <div className="pt-3">
              <label htmlFor="remarks">Remarks</label>
              <select className="form-select" name="remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)}>
                <option value="Collection Overdue">Collection Overdue</option>
              </select>
            </div>
            <div className="pt-3">
              <label htmlFor="reassignment">Reassignment</label>
              {
                reassignmentHistory ? (
                  <input
                    name="reassignmentHistory"
                    type="text"
                    value={reassignmentHistory}
                    className="form-control"
                    readOnly
                  />
                ) : (
                  <select className="form-select" name="reassignment" value={reassignment} onChange={(e) => setReassignment(e.target.value)}>
                    <option value="" disabled>Last Patient Number</option>
                    {reassignmentList.map((redemption, index) => <option key={index} value={redemption.id}>{redemption.patient.number.substr(-4)}</option>)}
                  </select>
                )
              }
            </div>
          </div>
          {
            loading ? (
              <div className="text-center">
                <CircularProgress />
              </div>
            ) : (
              <div className="text-center mt-4">
                <button type="button" className="btn btn-primary shadow-none admin-button" onClick={reassignRedemption}>CONFIRM</button>
              </div>
            )
          }
        </Modal.Body>
      </Modal>
    </div>
  );
}

const mapStateToProps = state => {
  return {
    token: state.admin.token,
    role: state.admin.role
  };
};

export default connect(mapStateToProps, null)(PendingRedemptionReport);
